import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Eye, EyeOff, LogOut, KeyRound, CheckCircle2, AlertCircle } from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import logo from '../assets/logo.png';

export default function ChangePasswordPage() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const forced = !!user?.must_change_password;
  const mismatch = next && confirm && next !== confirm;
  const tooShort = next && next.length < 8;
  const canSubmit = current && next && confirm && !mismatch && !tooShort && !loading;

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (mismatch) { setError('Las contraseñas nuevas no coinciden.'); return; }
    if (tooShort) { setError('La nueva contraseña debe tener al menos 8 caracteres.'); return; }
    setLoading(true);
    try {
      await api.post('/auth/change-password', { current_password: current, new_password: next });
      setSuccess(true);
      // Reload user (clears must_change_password) then navigate.
      await refresh();
      setTimeout(() => {
        if (user?.role === 'admin' || user?.role === 'council') navigate('/admin', { replace: true });
        else navigate('/es/mi-perfil', { replace: true });
      }, 1200);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'No se pudo cambiar la contraseña. Verifica tu contraseña actual.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-charcoal-50 via-white to-cmic-50 flex flex-col" data-testid="change-password-page">
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <img src={logo} alt="CMIC" className="h-14 mx-auto mb-3" />
            <h1 className="text-2xl font-bold text-charcoal-800 flex items-center justify-center gap-2">
              <KeyRound size={22} className="text-cmic-500" /> Cambiar contraseña
            </h1>
            <p className="text-sm text-charcoal-500 mt-1">
              {forced
                ? 'Tu administrador requiere que cambies tu contraseña antes de continuar.'
                : 'Define una nueva contraseña para tu cuenta del CMIC.'}
            </p>
          </div>

          {forced && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-5 flex items-start gap-2.5 text-sm text-amber-900">
              <ShieldCheck size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <span>
                <strong>Cambio obligatorio</strong> — debes establecer tu propia contraseña antes de acceder al portal.
              </span>
            </div>
          )}

          {success ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-6 text-center" data-testid="change-password-success">
              <CheckCircle2 size={32} className="text-emerald-600 mx-auto mb-2" />
              <p className="text-emerald-900 font-semibold">¡Listo!</p>
              <p className="text-emerald-700 text-sm mt-1">Te llevamos a tu portal…</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-md border border-gray-100 p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-charcoal-600 mb-1.5">Contraseña actual</label>
                <div className="relative">
                  <input
                    type={showCurrent ? 'text' : 'password'}
                    value={current}
                    onChange={(e) => setCurrent(e.target.value)}
                    required
                    autoFocus
                    autoComplete="current-password"
                    data-testid="cp-current"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                  />
                  <button type="button" onClick={() => setShowCurrent((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-charcoal-400 hover:text-charcoal-600">
                    {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-charcoal-600 mb-1.5">Nueva contraseña <span className="text-charcoal-400 font-normal">(mín. 8 caracteres)</span></label>
                <div className="relative">
                  <input
                    type={showNew ? 'text' : 'password'}
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    data-testid="cp-new"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                  />
                  <button type="button" onClick={() => setShowNew((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-charcoal-400 hover:text-charcoal-600">
                    {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {tooShort && <p className="text-[11px] text-rose-600 mt-1 flex items-center gap-1"><AlertCircle size={12} /> Mínimo 8 caracteres.</p>}
              </div>

              <div>
                <label className="block text-xs font-semibold text-charcoal-600 mb-1.5">Confirmar nueva contraseña</label>
                <input
                  type={showNew ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  autoComplete="new-password"
                  data-testid="cp-confirm"
                  className={`w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500 ${
                    mismatch ? 'border-rose-300 bg-rose-50/30' : 'border-gray-200'
                  }`}
                />
                {mismatch && <p className="text-[11px] text-rose-600 mt-1 flex items-center gap-1"><AlertCircle size={12} /> Las contraseñas no coinciden.</p>}
                {!mismatch && confirm && next === confirm && (
                  <p className="text-[11px] text-emerald-600 mt-1 flex items-center gap-1"><CheckCircle2 size={12} /> Las contraseñas coinciden.</p>
                )}
              </div>

              {error && (
                <div className="bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg px-3 py-2" data-testid="cp-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                data-testid="cp-submit"
                className="w-full bg-cmic-500 hover:bg-cmic-600 disabled:bg-charcoal-300 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-lg py-2.5 transition-colors"
              >
                {loading ? 'Guardando…' : 'Cambiar contraseña'}
              </button>

              {forced && (
                <button
                  type="button"
                  onClick={() => { logout(); navigate('/login', { replace: true }); }}
                  data-testid="cp-logout"
                  className="w-full inline-flex items-center justify-center gap-2 text-xs text-charcoal-500 hover:text-rose-600 mt-1"
                >
                  <LogOut size={13} /> Cerrar sesión
                </button>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
